import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { PRODUCTS_QUERY, storefrontApiRequest, type ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { displayBrand, getProductFacts, matchesAgeBucket, matchesBabyBucket } from "@/lib/product-facts";
import { Loader2 } from "lucide-react";

const CATEGORY_IDS = [
  "all",
  "age-0-1",
  "age-1-3",
  "age-3-6",
  "lahja",
  "iglu",
  "climbing",
  "tower",
  "table",
  "storage",
  "toys",
  "softplay",
] as const;
type CategoryId = (typeof CATEGORY_IDS)[number];

const SORT_IDS = ["suositellut", "halvin", "kallein", "nimi", "ika"] as const;
type SortId = (typeof SORT_IDS)[number];
const PRICE_IDS = ["all", "alle100", "100-200", "yli200"] as const;
type PriceId = (typeof PRICE_IDS)[number];

// NOTE: fields are `.optional()` (no `.default()`) on purpose. TanStack
// Router redirects (307) when the validated search object serializes back
// to a URL that differs from the incoming one — Zod defaults would force
// every naked /kauppa visit to redirect to
// /kauppa?kategoria=all&jarjesta=suositellut&…, costing crawlers an extra
// hop. Defaults are applied at read time via `withDefaults()` below.
const kauppaSearchSchema = z.object({
  kategoria: fallback(z.enum(CATEGORY_IDS).optional(), undefined),
  jarjesta: fallback(z.enum(SORT_IDS).optional(), undefined),
  hinta: fallback(z.enum(PRICE_IDS).optional(), undefined),
  saatavilla: fallback(z.boolean().optional(), undefined),
  taitettava: fallback(z.boolean().optional(), undefined),
  rampilla: fallback(z.boolean().optional(), undefined),
  modulaarinen: fallback(z.boolean().optional(), undefined),
  haku: fallback(z.string().optional(), undefined),
  market: fallback(z.enum(["fi", "us"]).optional(), undefined),
});
type KauppaSearchRaw = z.infer<typeof kauppaSearchSchema>;
type KauppaSearch = {
  kategoria: CategoryId;
  jarjesta: SortId;
  hinta: PriceId;
  saatavilla: boolean;
  taitettava: boolean;
  rampilla: boolean;
  modulaarinen: boolean;
  haku: string;
  market: "fi" | "us";
};
const withDefaults = (s: KauppaSearchRaw): KauppaSearch => ({
  kategoria: s.kategoria ?? "all",
  jarjesta: s.jarjesta ?? "suositellut",
  hinta: s.hinta ?? "all",
  saatavilla: s.saatavilla ?? false,
  taitettava: s.taitettava ?? false,
  rampilla: s.rampilla ?? false,
  modulaarinen: s.modulaarinen ?? false,
  haku: s.haku ?? "",
  market: s.market ?? "fi",
});

const productsQueryOptions = (market: "fi" | "us") => ({
  queryKey: ["products", market] as const,
  queryFn: async () => {
    const country = market === "us" ? "US" : "FI";
    const language = "EN"; // primary locale holds curated Finnish copy
    const res = await storefrontApiRequest(PRODUCTS_QUERY, {
      first: 100,
      query: null,
      country,
      language,
    });
    return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
  },
});

export const Route = createFileRoute("/kauppa")({
  validateSearch: zodValidator(kauppaSearchSchema),
  loaderDeps: ({ search }) => ({ market: search.market ?? "fi" }),
  loader: ({ context, deps }) => {
    // Await so SSR (and Googlebot) gets the rendered product list,
    // not an empty shell that only fills in after client hydration.
    return context.queryClient.ensureQueryData(productsQueryOptions(deps.market));
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "Montessori-kauppa: Pikler & oppimistornit | Montessori24" },
      { name: "description", content: "Pikler-kiipeilytelineet, oppimistornit, lasten vaaterekit ja lelulaatikot suomalaisiin koteihin. Ilmainen toimitus 3–7 pv." },
      { property: "og:title", content: "Montessori-kauppa — Pikler-telineet, oppimistornit & vaaterekit" },
      { property: "og:description", content: "Montessori-kalusteet ja -lelut: Pikler-telineet, oppimistornit, vaaterekit ja lelulaatikot. Ilmainen toimitus Suomeen 3–7 pv." },
      { property: "og:url", content: "https://montessori24.fi/kauppa" },
      { property: "og:image", content: "https://montessori24.fi/og-image.jpg" },
      { name: "twitter:image", content: "https://montessori24.fi/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://montessori24.fi/kauppa" },
      { rel: "alternate", hrefLang: "fi", href: "https://montessori24.fi/kauppa" },
      { rel: "alternate", hrefLang: "x-default", href: "https://montessori24.fi/kauppa" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Montessori-kauppa — Montessori24",
          url: "https://montessori24.fi/kauppa",
          inLanguage: "fi-FI",
        }),
      },
      ...(() => {
        // ItemList of the catalogue: Finnish product names with live
        // price and availability so listing SERPs carry offer data too.
        const items = (loaderData ?? []).slice(0, 60).map((e, i) => {
          const n = e.node;
          const v = n.variants?.edges?.[0]?.node;
          return {
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              "@id": `https://montessori24.fi/product/${n.handle}#product`,
              name: n.title,
              url: `https://montessori24.fi/product/${n.handle}`,
              image: n.images?.edges?.[0]?.node?.url,
              brand: { "@type": "Brand", name: displayBrand(n.vendor) ?? "Montessori24" },
              offers: {
                "@type": "Offer",
                priceCurrency: v?.price?.currencyCode ?? n.priceRange.minVariantPrice.currencyCode,
                price: v?.price?.amount ?? n.priceRange.minVariantPrice.amount,
                availability: v?.availableForSale
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
                url: `https://montessori24.fi/product/${n.handle}`,
              },
            },
          };
        });
        if (!items.length) return [];
        return [{
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Montessori-kaupan tuotteet",
            inLanguage: "fi-FI",
            numberOfItems: items.length,
            itemListElement: items,
          }),
        }];
      })(),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
            { "@type": "ListItem", position: 2, name: "Montessori-kauppa", item: "https://montessori24.fi/kauppa" },
          ],
        }),
      },
    ],
  }),
  component: Shop,
});

function Shop() {
  const rawSearch = Route.useSearch();
  const search = useMemo(() => withDefaults(rawSearch), [rawSearch]);
  const { kategoria, jarjesta, hinta, saatavilla, taitettava, rampilla, modulaarinen, haku, market } = search;
  const { data } = useSuspenseQuery(productsQueryOptions(market));
  const isLoading = false;
  const navigate = useNavigate({ from: "/kauppa" });
  const products = data ?? [];

  // Backwards compatibility: redirect legacy hash URLs (#climbing, #ikae-1-3, …)
  // to the new stable ?kategoria= search param so old shared links still work.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = window.location.hash.replace("#", "");
    if (!h) return;
    let next: CategoryId | null = null;
    if (h.startsWith("lahja")) next = "lahja";
    else if (h.startsWith("ikae-0-1")) next = "age-0-1";
    else if (h.startsWith("ikae-1-3")) next = "age-1-3";
    else if (h.startsWith("ikae-3-6")) next = "age-3-6";
    else if ((CATEGORY_IDS as readonly string[]).includes(h)) next = h as CategoryId;
    if (next) {
      void navigate({ search: { kategoria: next }, hash: "", replace: true });
    }
  }, [navigate]);

  const categories = useMemo<
    Array<{ id: string; label: string; match: (title: string, vendor?: string, handle?: string) => boolean }>
  >(
    () => [
      { id: "all", label: "Kaikki", match: (_t: string, _v?: string, _h?: string) => true },
      // Benefit-led / age-led (primary). Titles are returned in Finnish via
      // @inContext(language: FI) so match against Finnish words (with English
      // fallbacks for any untranslated items).
      // Age buckets use the manufacturer's own recommendation from the verified
      // fact sheet — never a keyword guess from the product title. Products
      // without a verified age are intentionally absent from these filters.
      { id: "age-0-1", label: "Ikä 0–1 v", match: (t: string, v?: string, h?: string) => matchesBabyBucket(h ?? "", t, v) },
      { id: "age-1-3", label: "Ikä 1–3 v", match: (_t: string, _v?: string, h?: string) => matchesAgeBucket(h ?? "", 12, 36) },
      { id: "age-3-6", label: "Ikä 3–6 v", match: (_t: string, _v?: string, h?: string) => matchesAgeBucket(h ?? "", 36, 72) },
      { id: "lahja", label: "Lahjaideoita 🎁", match: (t: string) => /sateenkaari|rainbow|keinutuoli|kirja|kirjateline|kirjanäyttö|sensoritaulu|busy board|stacker|puzzle|matto|book|wobble|gift/i.test(t) },
      { id: "iglu", label: "IGLU Blocks ✨", match: (_t: string, v?: string) => /iglu/i.test(v ?? "") },
      // Category-led (secondary). Match Finnish translations + English fallbacks.
      { id: "climbing", label: "Kiipeilytelineet", match: (t: string) => /kiipeil|kiipeily|kolmio|kaari|kolmikulm|3-?kulm|climbing|climber|triangle|arch|3-?angle/i.test(t) },
      { id: "tower", label: "Oppimistornit", match: (t: string) => /torni|apujakkar|jakkar|helper tower|kitchen tower|tower|stool/i.test(t) },
      { id: "table", label: "Pöydät & tuolit", match: (t: string) => /pöyt|pöytätaso|tuoli|keinutuoli|vieroitus|leikkiteline|weaning|table|chair|play stand/i.test(t) },
      { id: "storage", label: "Säilytys", match: (t: string) => /hylly|vaateteline|vaaterekki|säilytys|säilytystila|laatikko|lelulaatikko|kuivausteline|kirjateline|kirjanäyttö|lastenhyllykk|clothing rack|shelf|shelves|storage|hanger|book display|toy box/i.test(t) },
      { id: "toys", label: "Puulelut & pelit", match: (t: string, v?: string) => /wooden story|oakhill|leg-and-go|leg&go|ette-tete/i.test(v ?? "") || /puulel|puinen|puiset|palapel|palikk|helist|purulel|vetolel|muotolajitt|tangram|pyramidi|numerotaul|pingismail|kiinniottope|pétanque|petanque|juna|akrobaatti|nukke|doll|puzzle|pounding|spinner|tic-?tac-?toe|potkupyör|piirtotaul|ruokarekk|pesupiste|tarjottim|tasapainopuom|vauvakuntoil|kiipeilykuutio|cubitri|rocking chair|keinutuoli|sateenkaarisilta|zigzag/i.test(t) },
      { id: "softplay", label: "Pehmoleikki", match: (t: string) => /soft ?play|pehmo|pehmeä|pallomeri|ball ?pit|foam|vaahtomuov|mega ?wave|megalaine|leikkikiila|kiila|leikkikuutio/i.test(t) },
    ],
    []
  );

  const active = categories.find((c) => c.id === kategoria) ?? categories[0];

  // SEO/UX: when a specific category is active, surface links to the
  // dedicated landing page + related guides. Boosts internal linking and
  // gives shoppers a route to comparison content without leaving flow.
  const categoryLandingMap: Partial<Record<CategoryId, { landing: string; landingLabel: string; guides: Array<{ to: string; label: string }> }>> = {
    climbing: {
      landing: "/kiipeilyteline",
      landingLabel: "Kiipeilyteline lapsille — opas ja mallit",
      guides: [
        { to: "/pikler-kolmio", label: "Pikler-kolmio" },
        { to: "/kiipeilyteline-pihalle", label: "Kiipeilyteline pihalle" },
        { to: "/montessori-koulu/pikler-kiipeilyteline-opas", label: "Pikler-opas" },
        { to: "/montessori-koulu/kiipeilykolmio-lapselle-ikaopas", label: "Kiipeilykolmio: ikäopas" },
      ],
    },
    tower: {
      landing: "/oppimistorni",
      landingLabel: "Oppimistorni — opas ja mallit",
      guides: [
        { to: "/taaperotorni", label: "Taaperotorni" },
        { to: "/oppimistorni-opas", label: "Oppimistorni-opas" },
        { to: "/montessori-koulu/oppimistorni-turvallisuus", label: "Turvallisuusopas" },
        { to: "/montessori-koulu/lapsi-keittiossa-ikaopas", label: "Lapsi keittiössä (ikäopas)" },
      ],
    },
    storage: {
      landing: "/vaaterekki",
      landingLabel: "Vaaterekki ja säilytys — opas",
      guides: [
        { to: "/lasten-vaaterekki", label: "Lasten vaaterekki" },
        { to: "/montessori-koulu/montessori-kalusteet-kotiin", label: "Kalusteopas" },
        { to: "/montessori-koulu/montessori-vaatehuolto-opas", label: "Vaatehuolto-opas" },
      ],
    },
    iglu: {
      landing: "/iglu",
      landingLabel: "IGLU-palikat — kaikki setit",
      guides: [
        { to: "/montessori-lelut", label: "Montessori-lelut" },
      ],
    },
    table: {
      landing: "/montessori-lelut",
      landingLabel: "Pöydät, tuolit ja lasten kalusteet",
      guides: [
        { to: "/montessori-koulu/montessori-kalusteet-kotiin", label: "Kalusteopas" },
      ],
    },
    toys: {
      landing: "/montessori-lelut",
      landingLabel: "Montessori-lelut — puulelut ja pelit",
      guides: [
        { to: "/iglu", label: "IGLU-palikat" },
        { to: "/montessori-vauva", label: "Montessori-vauva 0–12 kk" },
      ],
    },
    softplay: {
      landing: "/kauppa",
      landingLabel: "Pehmoleikki — softplay-setit",
      guides: [
        { to: "/montessori-vauva", label: "Montessori-vauva 0–12 kk" },
        { to: "/montessori-koulu/montessori-lattiapatja", label: "Lattiapatja-opas" },
      ],
    },
  };
  const activeLanding = categoryLandingMap[kategoria as CategoryId];

  const priceMatch = (amount: number) => {
    if (hinta === "alle100") return amount < 100;
    if (hinta === "100-200") return amount >= 100 && amount <= 200;
    if (hinta === "yli200") return amount > 200;
    return true;
  };

  const featureMatch = (title: string) => {
    if (taitettava && !/taitett|foldable|folding/i.test(title)) return false;
    if (rampilla && !/ramp/i.test(title)) return false;
    if (modulaarinen && !/iglu|modul|sarja|paket|set\b/i.test(title)) return false;
    return true;
  };

  const searchQ = haku.trim().toLowerCase();
  const searchMatch = (title: string, description: string) => {
    if (!searchQ) return true;
    return title.toLowerCase().includes(searchQ) || description.toLowerCase().includes(searchQ);
  };

  // Sort key by recommended age: lower = younger. Uses the manufacturer's
  // verified minimum age when available; keyword heuristics only order the
  // remainder, which is never presented as a verified age.
  const ageRank = (title: string, vendor?: string, handle?: string) => {
    const verified = getProductFacts(handle).ageMinMonths;
    if (verified != null) return verified;
    if (/iglu/i.test(vendor ?? "")) return 1000;
    if (/pehmo|pehmeä|pallomeri|matto|kiila|wobble|rainbow|sateenkaari|sensor|mega ?wave|megalaine/i.test(title)) return 1000;
    if (/kiipeil|kiipeily|kolmio|kaari|ramp|torni|apujakkar|jakkar|vieroitus|pöyt|tuoli|kirja|leikkiteline|leluhylly|pikler|climbing|climber|tower|table|chair|book|stool/i.test(title)) return 1001;
    if (/hylly|keittiö|liitutaul|desk|easel|kitchen|playhouse|shelf/i.test(title)) return 1002;
    return 1003;
  };

  const filtered = products.filter((p) => {
    const n = p.node;
    const vendor = (n as unknown as { vendor?: string }).vendor;
    if (!active.match(n.title, vendor, n.handle)) return false;
    const variant = n.variants.edges[0]?.node;
    if (saatavilla && !variant?.availableForSale) return false;
    const amount = parseFloat(n.priceRange.minVariantPrice.amount);
    if (!priceMatch(amount)) return false;
    if (!featureMatch(n.title)) return false;
    if (!searchMatch(n.title, n.description ?? "")) return false;
    return true;
  });

  const visible = [...filtered].sort((a, b) => {
    const pa = parseFloat(a.node.priceRange.minVariantPrice.amount);
    const pb = parseFloat(b.node.priceRange.minVariantPrice.amount);
    if (jarjesta === "halvin") return pa - pb;
    if (jarjesta === "kallein") return pb - pa;
    if (jarjesta === "nimi") return a.node.title.localeCompare(b.node.title, "fi");
    if (jarjesta === "ika") {
      const va = (a.node as unknown as { vendor?: string }).vendor;
      const vb = (b.node as unknown as { vendor?: string }).vendor;
      const ra = ageRank(a.node.title, va, a.node.handle);
      const rb = ageRank(b.node.title, vb, b.node.handle);
      if (ra !== rb) return ra - rb;
      return a.node.title.localeCompare(b.node.title, "fi");
    }
    return 0;
  });

  const filtersActive =
    hinta !== "all" || saatavilla || taitettava || rampilla || modulaarinen || jarjesta !== "suositellut" || haku !== "";

  const updateSearch = (patch: Partial<KauppaSearch>) =>
    navigate({
      search: ((prev: Partial<KauppaSearch>) => ({ ...prev, ...patch })) as never,
      replace: true,
      resetScroll: false,
    });

  const ageCats = categories.filter((c) => c.id.startsWith("age-"));
  const productCats = categories.filter(
    (c) => !c.id.startsWith("age-") && c.id !== "all" && c.id !== "lahja" && c.id !== "iglu"
  );
  const featuredCats = categories.filter((c) => c.id === "lahja" || c.id === "iglu");

  const renderChip = (c: typeof categories[number]) => {
    const count = products.filter((p) =>
      c.match(p.node.title, (p.node as unknown as { vendor?: string }).vendor, p.node.handle)
    ).length;
    if (count === 0) return null;
    const isActive = c.id === kategoria;
    return (
      <Link
        key={c.id}
        to="/kauppa"
        search={((prev: Partial<KauppaSearch>) => ({ ...prev, kategoria: c.id as CategoryId })) as never}
        replace
        resetScroll={false}
        aria-current={isActive ? "page" : undefined}
        className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
          isActive
            ? "border-foreground bg-foreground text-background"
            : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
        }`}
      >
        {c.label}
        <span className="ml-1.5 text-xs opacity-60">{count}</span>
      </Link>
    );
  };

  const toggleChipClass = (on: boolean) =>
    `rounded-full border px-4 py-1.5 text-sm transition-colors ${
      on
        ? "border-foreground bg-foreground text-background"
        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
    }`;

  const priceLabel: Record<PriceId, string> = {
    all: "Kaikki",
    alle100: "Alle 100 €",
    "100-200": "100–200 €",
    yli200: "Yli 200 €",
  };
  const sortLabel: Record<SortId, string> = {
    suositellut: "Suositellut",
    ika: "Ikä (nuorin ensin)",
    halvin: "Halvin ensin",
    kallein: "Kallein ensin",
    nimi: "Nimi A–Ö",
  };
  const activeFeatureLabels = [
    saatavilla && "Varastossa",
    taitettava && "Taitettava",
    rampilla && "Rampilla",
    modulaarinen && "Modulaarinen",
  ].filter(Boolean) as string[];

  const summaryClass =
    "group/summary flex cursor-pointer list-none items-center justify-between gap-3 py-3 text-left [&::-webkit-details-marker]:hidden";
  const summaryLabel = (label: string, value?: string) => (
    <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      {value && <span className="text-sm text-foreground">{value}</span>}
    </span>
  );
  const chevron = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 7.5l5 5 5-5" />
    </svg>
  );

  return (
    <Layout>
      <section className="container-prose pt-16 pb-12 md:pt-24">
        <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Mallisto</span>
        <h1 className="mt-3 text-5xl text-foreground md:text-6xl text-balance">Montessori-kauppa</h1>
        <p className="mt-5 max-w-xl text-muted-foreground leading-relaxed">
          Kaikki Montessori-huonekalut lastenhuoneeseen —{" "}
          <Link to="/pikler-kolmio" className="text-foreground underline underline-offset-4 hover:text-clay">Pikler-kiipeilyteline</Link>,{" "}
          <Link to="/oppimistorni" className="text-foreground underline underline-offset-4 hover:text-clay">oppimistorni</Link>,{" "}
          <Link to="/montessori-sanky" className="text-foreground underline underline-offset-4 hover:text-clay">sänky</Link> ja{" "}
          <Link to="/vaaterekki" className="text-foreground underline underline-offset-4 hover:text-clay">vaaterekki</Link>. Ilmainen toimitus 3–7 arkipäivässä.
        </p>
        <details className="group mt-6 max-w-xl">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground">
            Oppaat ja kategoriat
            <span className="transition-transform group-open:rotate-45">+</span>
          </summary>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { to: "/taaperotorni", label: "Taaperotorni" },
              { to: "/kiipeilyteline", label: "Kiipeilyteline sisälle" },
              { to: "/kiipeilyteline-pihalle", label: "Kiipeilyteline pihalle" },
              { to: "/lasten-vaaterekki", label: "Lasten vaaterekki" },
              { to: "/lelulaatikko", label: "Lelulaatikko" },
              { to: "/iglu", label: "IGLU-palikat" },
              { to: "/montessori-lelut", label: "Montessori-lelut" },
              { to: "/montessori-vauva", label: "Montessori-vauva 0–12 kk" },
              { to: "/lastenhuoneen-kalusteet", label: "Lastenhuoneen kalusteet" },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="rounded-full border border-border px-3.5 py-1.5 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
              >
                {c.label}
              </Link>
            ))}
          </div>
        </details>
      </section>

      {products.length > 0 && (
        <section className="border-y border-border bg-cream/60">
          <div className="container-prose py-6 md:py-8">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <label htmlFor="kauppa-haku" className="shrink-0 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Haku</label>
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <input
                  id="kauppa-haku"
                  type="search"
                  value={haku}
                  onChange={(e) => updateSearch({ haku: e.target.value })}
                  placeholder="Etsi tuotetta nimellä…"
                  aria-label="Etsi tuotetta nimellä"
                  className="w-full rounded-full border border-border bg-background px-4 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none"
                />
                {haku && (
                  <button
                    type="button"
                    onClick={() => updateSearch({ haku: "" })}
                    aria-label="Tyhjennä haku"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <details className="group border-t border-border/60">
              <summary className={summaryClass}>
                {summaryLabel("Näytä", active.id === "all" ? "Kaikki" : active.label)}
                {chevron}
              </summary>
              <div className="flex flex-wrap gap-2 pb-4">
                {renderChip(categories[0])}
                {featuredCats.map(renderChip)}
              </div>
            </details>

            <details className="group border-t border-border/60">
              <summary className={summaryClass}>
                {summaryLabel(
                  "Ikä",
                  active.id.startsWith("age-") ? active.label : undefined
                )}
                {chevron}
              </summary>
              <div className="flex flex-wrap gap-2 pb-4">{ageCats.map(renderChip)}</div>
            </details>

            <details className="group border-t border-border/60">
              <summary className={summaryClass}>
                {summaryLabel(
                  "Tyyppi",
                  productCats.some((c) => c.id === active.id) ? active.label : undefined
                )}
                {chevron}
              </summary>
              <div className="flex flex-wrap gap-2 pb-4">{productCats.map(renderChip)}</div>
            </details>

            <details className="group border-t border-border/60">
              <summary className={summaryClass}>
                {summaryLabel("Hinta", hinta !== "all" ? priceLabel[hinta as PriceId] : undefined)}
                {chevron}
              </summary>
              <div className="flex flex-wrap gap-2 pb-4">
                {([
                  ["all", "Kaikki"],
                  ["alle100", "Alle 100 €"],
                  ["100-200", "100–200 €"],
                  ["yli200", "Yli 200 €"],
                ] as Array<[PriceId, string]>).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => updateSearch({ hinta: id })}
                    aria-pressed={hinta === id}
                    className={toggleChipClass(hinta === id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </details>

            <details className="group border-t border-border/60">
              <summary className={summaryClass}>
                {summaryLabel(
                  "Suodata",
                  activeFeatureLabels.length > 0 ? activeFeatureLabels.join(" · ") : undefined
                )}
                {chevron}
              </summary>
              <div className="flex flex-wrap gap-2 pb-4">
                <button
                  type="button"
                  onClick={() => updateSearch({ saatavilla: !saatavilla })}
                  aria-pressed={saatavilla}
                  className={toggleChipClass(saatavilla)}
                >
                  Varastossa
                </button>
                <button
                  type="button"
                  onClick={() => updateSearch({ taitettava: !taitettava })}
                  aria-pressed={taitettava}
                  className={toggleChipClass(taitettava)}
                >
                  Taitettava
                </button>
                <button
                  type="button"
                  onClick={() => updateSearch({ rampilla: !rampilla })}
                  aria-pressed={rampilla}
                  className={toggleChipClass(rampilla)}
                >
                  Rampilla
                </button>
                <button
                  type="button"
                  onClick={() => updateSearch({ modulaarinen: !modulaarinen })}
                  aria-pressed={modulaarinen}
                  className={toggleChipClass(modulaarinen)}
                >
                  Modulaarinen
                </button>
              </div>
            </details>

            <details className="group border-t border-border/60">
              <summary className={summaryClass}>
                {summaryLabel("Järjestä", sortLabel[jarjesta as SortId])}
                {chevron}
              </summary>
              <div className="flex flex-wrap gap-2 pb-4">
                {([
                  ["suositellut", "Suositellut"],
                  ["ika", "Ikä (nuorin ensin)"],
                  ["halvin", "Halvin ensin"],
                  ["kallein", "Kallein ensin"],
                  ["nimi", "Nimi A–Ö"],
                ] as Array<[SortId, string]>).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => updateSearch({ jarjesta: id })}
                    aria-pressed={jarjesta === id}
                    className={toggleChipClass(jarjesta === id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </details>

            {filtersActive && (
              <div className="border-t border-border/60 pt-3">
                <button
                  type="button"
                  onClick={() =>
                    updateSearch({
                      jarjesta: "suositellut",
                      hinta: "all",
                      saatavilla: false,
                      taitettava: false,
                      rampilla: false,
                      modulaarinen: false,
                      haku: "",
                    })
                  }
                  className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  Tyhjennä suodattimet
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="container-prose pt-10 pb-32">
        {activeLanding && (
          <div className="mb-8 rounded-2xl border border-border bg-cream/60 p-5 md:p-6">
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">Aiheeseen liittyvät oppaat</p>
            <Link
              to={activeLanding.landing}
              className="mt-2 inline-flex items-center gap-2 text-lg text-foreground underline underline-offset-4 hover:text-clay md:text-xl"
            >
              {activeLanding.landingLabel} →
            </Link>
            {activeLanding.guides.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {activeLanding.guides.map((g) => (
                  <Link
                    key={g.to}
                    to={g.to}
                    className="rounded-full border border-border bg-background px-4 py-1.5 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
                  >
                    {g.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
        {!isLoading && products.length > 0 && (
          <p className="mb-8 text-sm text-muted-foreground">
            {visible.length} {visible.length === 1 ? "tuote" : "tuotetta"}
            {kategoria !== "all" && <> · {active.label}</>}
            {filtersActive && <> · suodatettu</>}
          </p>
        )}
        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : products.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-cream py-20 text-center">
            <p className="text-foreground">Mallistoa päivitetään.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Tuotteet palaavat nähtäville pian. Voit sillä välin{" "}
              <a href="mailto:asiakaspalvelu@montessori24.fi" className="underline underline-offset-4 hover:text-foreground">
                ottaa yhteyttä asiakaspalveluun
              </a>.
            </p>
          </div>
        ) : visible.length === 0 ? (
          <div>
            <div className="rounded-xl border border-dashed border-border bg-cream py-14 text-center">
              <p className="text-foreground">Ei tuotteita näillä suodattimilla.</p>
              <button
                type="button"
                onClick={() =>
                  updateSearch({
                    jarjesta: "suositellut",
                    hinta: "all",
                    saatavilla: false,
                    taitettava: false,
                    rampilla: false,
                    modulaarinen: false,
                    haku: "",
                  })
                }
                className="mt-4 text-sm text-foreground underline underline-offset-4"
              >
                Tyhjennä suodattimet
              </button>
            </div>
            {products.length > 0 && (
              <div className="mt-12">
                <div className="mb-6 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">Suositukset sinulle</p>
                    <h2 className="mt-1 font-display text-2xl italic text-foreground md:text-3xl">Nämä ovat suosittuja juuri nyt</h2>
                  </div>
                  <Link to="/kauppa" search={{ kategoria: "all" }} className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
                    Näytä kaikki →
                  </Link>
                </div>
               <div className="grid gap-x-8 gap-y-20 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-10 xl:grid-cols-4 xl:gap-x-12">
                  {products.slice(0, 4).map((p) => (
                    <ProductCard
                      key={p.node.id}
                      product={p}
                      sizes="(min-width: 1280px) 280px, (min-width: 1024px) 33vw, (min-width: 640px) 45vw, 90vw"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-x-8 gap-y-20 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-10 xl:grid-cols-4 xl:gap-x-12">
            {visible.map((p) => (
              <ProductCard
                key={p.node.id}
                product={p}
                sizes="(min-width: 1280px) 280px, (min-width: 1024px) 33vw, (min-width: 640px) 45vw, 90vw"
              />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
